import type {
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  ResetPasswordRequest,
  SignupRequest,
  UpdateProfileRequest,
  UserData,
} from '../types/auth';
import { BACKEND_BASE_URL } from '../config/api';
import { defaultAuthConfig } from '../types/auth';
import { graphqlRequest } from './graphqlClient';

class AuthService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;
  private fallbackBaseUrl: string;

  constructor(config = defaultAuthConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
    this.fallbackBaseUrl = this.baseUrl.endsWith('/api') ? this.baseUrl : `${this.baseUrl}/api`;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private getFirstString(...values: unknown[]): string | undefined {
    for (const value of values) {
      const candidate = this.asString(value);
      if (candidate) {
        return candidate;
      }
    }
    return undefined;
  }

  private isPlaceholderDisplayName(value: unknown): boolean {
    const normalizedValue = this.asString(value)?.toLowerCase();
    return normalizedValue === 'reader' || normalizedValue === 'guest buyer';
  }

  private buildDisplayNameFromEmail(email?: string): string | undefined {
    const normalizedEmail = this.asString(email);
    const [namePart] = normalizedEmail?.split('@') ?? [];
    if (!namePart) {
      return undefined;
    }

    const formatted = namePart
      .split(/[._-]/g)
      .filter(Boolean)
      .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
      .join(' ');

    return formatted || undefined;
  }

  private normalizeAssetUrl(value: unknown): string | undefined {
    const rawValue = this.asString(value);
    if (!rawValue) {
      return undefined;
    }

    if (/^(https?:|data:|blob:)/i.test(rawValue)) {
      return rawValue;
    }

    const normalizedPath = rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
    return `${BACKEND_BASE_URL}${normalizedPath}`;
  }

  private normalizeProvider(value: unknown): UserData['provider'] | undefined {
    if (value === 'google' || value === 'apple' || value === 'email') {
      return value;
    }
    return undefined;
  }

  private normalizeRole(value: unknown): UserData['role'] {
    if (value === 'creator') {
      return 'vendor';
    }

    if (value === 'normal' || value === 'user') {
      return 'buyer';
    }

    if (value === 'staff') {
      return 'manager';
    }

    if (value === 'admin' || value === 'manager' || value === 'vendor' || value === 'buyer' || value === 'customer') {
      return value;
    }
    return 'customer';
  }

  private normalizeUser(userValue: unknown): UserData | null {
    const userRecord = this.asRecord(userValue);
    const id = this.getFirstString(userRecord.id, userRecord._id, userRecord.userId);
    if (!id) {
      return null;
    }

    const firstname = this.getFirstString(userRecord.firstname, userRecord.firstName);
    const lastname = this.getFirstString(userRecord.lastname, userRecord.lastName);
    const combinedName = [firstname, lastname].filter(Boolean).join(' ');
    const email =
      this.getFirstString(userRecord.email, userRecord.username) ?? `${id}@haapa.local`;
    const explicitName = this.getFirstString(userRecord.name, userRecord.fullname, userRecord.fullName);
    const fallbackName =
      this.getFirstString(combinedName, firstname, userRecord.username) ??
      this.buildDisplayNameFromEmail(email) ??
      'Reader';
    const name =
      explicitName && !this.isPlaceholderDisplayName(explicitName)
        ? explicitName
        : fallbackName;
    const fullname =
      this.getFirstString(
        this.isPlaceholderDisplayName(userRecord.fullname) ? undefined : userRecord.fullname,
        this.isPlaceholderDisplayName(userRecord.fullName) ? undefined : userRecord.fullName,
        combinedName,
        firstname,
        this.isPlaceholderDisplayName(userRecord.name) ? undefined : userRecord.name,
        name,
      ) ?? name;

    return {
      id,
      username: this.getFirstString(userRecord.username),
      name,
      fullname,
      email,
      role: this.normalizeRole(userRecord.role),
      provider: this.normalizeProvider(userRecord.provider),
      phone: this.getFirstString(userRecord.phone, userRecord.phoneNumber),
      telephone: this.getFirstString(userRecord.telephone),
      country: this.getFirstString(userRecord.country),
      preferredLanguage: this.getFirstString(userRecord.preferredLanguage) as UserData['preferredLanguage'] | undefined,
      audienceProfile: this.getFirstString(userRecord.audienceProfile) as UserData['audienceProfile'] | undefined,
      dateOfBirth: this.getFirstString(userRecord.dateOfBirth),
      profileImageUrl: this.normalizeAssetUrl(
        this.getFirstString(
        userRecord.profileImageUrl,
        userRecord.profileImage,
        userRecord.profile,
        userRecord.avatar,
        userRecord.imageUrl,
        userRecord.photoUrl,
        userRecord.file,
        userRecord.picture,
        userRecord.pictureFile,
      )),
    };
  }

  private normalizeAuthResponse(payload: unknown): AuthResponse {
    const responseRecord = this.asRecord(payload);
    const dataRecord = this.asRecord(responseRecord.data);

    const normalizedUser =
      this.normalizeUser(responseRecord.user) ??
      this.normalizeUser(dataRecord.user) ??
      null;

    const normalizedToken = this.getFirstString(
      responseRecord.token,
      responseRecord.accessToken,
      responseRecord.jwt,
      dataRecord.token,
      dataRecord.accessToken,
      dataRecord.jwt,
    );

    return {
      success: typeof responseRecord.success === 'boolean' ? responseRecord.success : true,
      message:
        this.getFirstString(responseRecord.message, dataRecord.message) ??
        undefined,
      error: this.getFirstString(responseRecord.error, dataRecord.error),
      user: normalizedUser ?? undefined,
      token: normalizedToken,
    };
  }

  private persistAuthData(result: AuthResponse): void {
    const token = result.token;
    if (token) {
      this.setToken(token);
    }

    if (result.user) {
      this.setStoredUser(result.user);
    }
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    if (!payload || typeof payload !== 'object') {
      return fallback;
    }

    const candidate = payload as { error?: unknown; message?: unknown };
    if (typeof candidate.error === 'string' && candidate.error.trim()) {
      return candidate.error;
    }
    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return candidate.message;
    }

    return fallback;
  }

  private getAuthHeaders(): HeadersInit {
    const token = this.getToken();
    const storedUser = this.getStoredUser();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': '*/*',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (storedUser?.id) {
      headers['x-user-id'] = storedUser.id;
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const fallback = `Request failed with status ${response.status}`;
      throw {
        success: false,
        status: response.status,
        message: this.toErrorMessage(data, fallback),
        error: typeof (data as { error?: unknown } | null)?.error === 'string' ? (data as { error: string }).error : undefined,
      };
    }

    return (data ?? {}) as T;
  }

  private async _loginViaGraphQL(data: LoginRequest): Promise<AuthResponse> {
    const mutation = `
      mutation login($email: String!, $password: String!) {
        login(email: $email, password: $password) {
          token
          user {
            id
            username
            email
            role
            firstname
            lastname
            name
          }
        }
      }
    `;

    const payload = await graphqlRequest<{ login?: { token?: string; user?: unknown } }>(
      mutation,
      data as unknown as Record<string, unknown>,
    );
    const normalizedUser = this.normalizeUser(payload?.login?.user) ?? undefined;
    return {
      success: true,
      message: 'Login successful',
      user: normalizedUser,
      token: payload?.login?.token,
    };
  }

  private async _signupViaGraphQL(data: SignupRequest): Promise<AuthResponse> {
    const mutation = `
      mutation signup(
        $username: String!
        $firstname: String!
        $lastname: String!
        $telephone: String!
        $email: String!
        $password: String!
      ) {
        signup(
          firstname: $firstname
          lastname: $lastname
          username: $username
          email: $email
          password: $password
          telephone: $telephone
        ) {
          message
        }
      }
    `;

    const variables = {
      username: data.username,
      firstname: data.firstname || data.fullname?.split(/\s+/)[0] || data.username,
      lastname: data.lastname || data.fullname?.split(/\s+/).slice(1).join(' ') || 'Reader',
      telephone: data.telephone,
      email: data.email,
      password: data.password,
    };

    const response = await graphqlRequest<{ signup?: { message?: string } }>(mutation, variables);
    return {
      success: true,
      message: response?.signup?.message ?? 'Account created successfully',
    };
  }

  // Token management
  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.tokenKey, token);
  }

  removeToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.tokenKey);
  }

  // User management
  getStoredUser(): UserData | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem(this.userKey);
    if (!userStr) return null;
    try {
      return this.normalizeUser(JSON.parse(userStr));
    } catch {
      return null;
    }
  }

  setStoredUser(user: UserData): void {
    if (typeof window === 'undefined') return;
    const normalizedUser = this.normalizeUser(user) ?? user;
    localStorage.setItem(this.userKey, JSON.stringify(normalizedUser));
  }

  removeStoredUser(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.userKey);
  }

  private async postWithFallback(path: string, body: unknown): Promise<unknown> {
    const urls = Array.from(new Set([`${this.baseUrl}${path}`, `${this.fallbackBaseUrl}${path}`]));
    let lastError: unknown = null;
    for (const url of urls) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
          },
          body: JSON.stringify(body),
        });
        const raw = await this.handleResponse<unknown>(response);
        return raw;
      } catch (error) {
        lastError = error;
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) {
          break;
        }
      }
    }
    throw lastError ?? new Error('Request failed');
  }

  // Auth API methods
  async signup(data: SignupRequest): Promise<AuthResponse> {
    const firstname = data.firstname?.trim() || data.fullname?.trim().split(/\s+/)[0] || data.username.trim();
    const lastname = data.lastname?.trim() || data.fullname?.trim().split(/\s+/).slice(1).join(' ') || 'Reader';

    try {
      const raw = await this.postWithFallback('/auth/signup', {
        username: data.username,
        firstname,
        lastname,
        telephone: data.telephone,
        email: data.email,
        password: data.password,
        role: data.role === 'vendor' ? 'creator' : data.role,
        audienceProfile: data.audienceProfile,
        preferredLanguage: data.preferredLanguage,
      });

      const result = this.normalizeAuthResponse(raw);
      this.persistAuthData(result);
      return result;
    } catch (error) {
      return {
        success: false,
        message: this.toErrorMessage(error, 'Registration failed. Please try again.'),
      };
    }
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const normalizedIdentifier = data.email.trim();
    const loginPayload: Record<string, unknown> = {
      ...data,
      email: normalizedIdentifier,
      identifier: normalizedIdentifier,
    };

    if (!normalizedIdentifier.includes('@')) {
      loginPayload.username = normalizedIdentifier;
    }

    const raw = await this.postWithFallback('/auth/login', loginPayload);
    const result = this.normalizeAuthResponse(raw);

    // bn backend returns { ok, data: { user, token } }
    if (!result.token) {
      const record = this.asRecord(raw);
      const dataRecord = this.asRecord(record.data);
      const token = this.asString(dataRecord.token);
      if (token) {
        result.token = token;
      }
    }

    if (!result.user) {
      const record = this.asRecord(raw);
      const dataRecord = this.asRecord(record.data);
      const user = this.normalizeUser(dataRecord.user);
      if (user) {
        result.user = user;
      }
    }

    this.persistAuthData(result);
    return result;
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
    } catch {
      // Continue with local logout even if API fails
    } finally {
      this.removeToken();
      this.removeStoredUser();
    }
  }

  async getCurrentUser(): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/auth/me`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const raw = await this.handleResponse<unknown>(response);
    const result = this.normalizeAuthResponse(raw);
    this.persistAuthData(result);
    return result;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<AuthResponse> {
    const fallbackName = data.name?.trim() || data.fullname?.trim() || '';
    const firstname = data.firstname?.trim() || fallbackName.split(/\s+/)[0] || undefined;
    const lastname = data.lastname?.trim() || fallbackName.split(/\s+/).slice(1).join(' ') || undefined;
    const profileImageValue =
      data.profileImageUrl?.trim() ||
      data.file?.trim() ||
      data.profile?.trim() ||
      data.picture?.trim() ||
      data.pictureFile?.trim() ||
      undefined;
    const response = await fetch(`${this.baseUrl}/auth/me`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        firstname,
        lastname,
        telephone: data.telephone ?? data.phone,
        audienceProfile: data.audienceProfile,
        preferredLanguage: data.preferredLanguage,
        country: data.country,
        dateOfBirth: data.dateOfBirth,
        profileImageUrl: profileImageValue,
        file: profileImageValue,
        profile: profileImageValue,
        picture: profileImageValue,
        pictureFile: profileImageValue,
      }),
    });

    const raw = await this.handleResponse<unknown>(response);
    const result = this.normalizeAuthResponse(raw);
    this.persistAuthData(result);
    return result;
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<AuthResponse> {
    const normalizedEmail = data.email.trim().toLowerCase();

    const sendRequest = async (payload: Record<string, string>): Promise<AuthResponse> => {
      const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
        },
        body: JSON.stringify(payload),
      });

      const raw = await this.handleResponse<unknown>(response);
      return this.normalizeAuthResponse(raw);
    };

    try {
      return await sendRequest({ email: normalizedEmail });
    } catch (error) {
      const errorMessage = this.toErrorMessage(error, '');
      const shouldRetryWithFallbackName =
        /fullname/i.test(errorMessage) && /required/i.test(errorMessage);

      if (!shouldRetryWithFallbackName) {
        throw error;
      }

      const fallbackFullname =
        data.fullname?.trim() ||
        normalizedEmail.split('@')[0]?.replace(/[._-]+/g, ' ').trim() ||
        'User';

      return sendRequest({
        email: normalizedEmail,
        fullname: fallbackFullname,
        fullName: fallbackFullname,
        name: fallbackFullname,
      });
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<AuthResponse> {
    const normalizedToken = data.token?.trim() || data.code?.trim() || '';
    const normalizedEmail = data.email?.trim().toLowerCase();
    const payload: Record<string, string> = {
      newPassword: data.newPassword,
    };

    if (normalizedToken) {
      payload.token = normalizedToken;
      payload.code = normalizedToken;
    }

    if (normalizedEmail) {
      payload.email = normalizedEmail;
    }

    const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': '*/*',
      },
      body: JSON.stringify(payload),
    });

    const raw = await this.handleResponse<unknown>(response);
    return this.normalizeAuthResponse(raw);
  }

  async loginWithGoogle(payload: { idToken?: string; token?: string } = {}): Promise<AuthResponse> {
    const normalizedToken = payload.idToken?.trim() || payload.token?.trim();
    const requestBody = normalizedToken
      ? {
          idToken: normalizedToken,
          token: normalizedToken,
        }
      : {};
    const normalizedBaseUrl = this.baseUrl.replace(/\/+$/, '');
    const baseUrlWithoutApiSuffix = normalizedBaseUrl.replace(/\/api$/i, '');

    const candidateUrls = Array.from(
      new Set([
        `${normalizedBaseUrl}/auth/google`,
        `${baseUrlWithoutApiSuffix}/api/auth/google`,
      ]),
    );

    let lastError: unknown = null;

    for (let index = 0; index < candidateUrls.length; index += 1) {
      const url = candidateUrls[index];
      const hasNextPath = index < candidateUrls.length - 1;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': '*/*',
          },
          body: JSON.stringify(requestBody),
        });

        if ((response.status === 404 || response.status === 405) && hasNextPath) {
          continue;
        }

        const raw = await this.handleResponse<unknown>(response);
        const result = this.normalizeAuthResponse(raw);
        this.persistAuthData(result);
        return result;
      } catch (error: unknown) {
        lastError = error;
      }
    }

    if (lastError instanceof Error) {
      throw lastError;
    }

    throw {
      success: false,
      message: this.toErrorMessage(lastError, 'Google authentication failed'),
      error: this.toErrorMessage(lastError, 'Google authentication failed'),
    };
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken() || !!this.getStoredUser()?.id;
  }

  // Clear all auth data
  clearAuth(): void {
    this.removeToken();
    this.removeStoredUser();
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing or custom configurations
export { AuthService };
